import React from 'react';
import { Link } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { MessageCircle, Plus } from 'lucide-react';
import { t } from '../../utils/i18n';
import { useDmPersona } from '../../context/DmPersonaContext';
import { listConversations } from '../../api/dm';
import UserAvatar from '../../components/UserAvatar';

const PAGE_SIZE = 30;

function formatRelative(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const diff = Date.now() - date.getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return date.toLocaleDateString();
}

function normalizePreviewText(text) {
  return String(text || '')
    .replace(/\s+/g, ' ')
    .trim();
}

function getPreviewText(item) {
  const latest = item?.latestMessage;
  const type = latest?.messageType;
  const text = normalizePreviewText(latest?.messageText);

  if (type === 'TEXT' && text) return text;
  if (type === 'IMAGE') return t('dm.conversations.preview.image');
  if (type === 'SYSTEM') return t('dm.conversations.preview.system');
  if (Number.isFinite(item?.lastMessageSeq) && item.lastMessageSeq > 0) {
    return t('dm.conversations.preview.seqFallback', { seq: item.lastMessageSeq });
  }
  return t('dm.conversations.preview.empty');
}

export default function DmConversations() {
  const { accessToken, userId } = useDmPersona();

  const query = useInfiniteQuery({
    queryKey: ['dm', 'conversations', userId],
    queryFn: ({ pageParam }) =>
      listConversations({ limit: PAGE_SIZE, beforeUpdatedAt: pageParam, callerUserId: userId }, accessToken),
    getNextPageParam: (last) => last.nextBeforeCursor || undefined,
    initialPageParam: undefined,
    refetchOnWindowFocus: true,
    staleTime: 15 * 1000,
  });

  const allItems = (query.data?.pages || []).flatMap((p) => p.conversations);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-white">
          {t('dm.conversations.title')}
        </h1>
        <Link
          to="/dm/new"
          className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg bg-primary text-black text-sm font-bold hover:bg-primary/90 transition-colors"
        >
          <Plus size={16} />
          {t('dm.conversations.newDm')}
        </Link>
      </div>

      {query.isLoading ? (
        <div className="text-center py-16 text-dark-text-secondary">
          {t('dm.conversations.loading')}
        </div>
      ) : query.isError ? (
        <div className="text-center py-12 px-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
          {query.error?.message || 'Error loading conversations'}
        </div>
      ) : allItems.length === 0 ? (
        <div className="text-center py-16 px-6 rounded-2xl bg-dark-card border border-dark-border">
          <MessageCircle size={32} className="mx-auto mb-3 text-dark-text-muted" />
          <p className="text-sm text-dark-text-secondary mb-4">{t('dm.conversations.empty')}</p>
          <Link
            to="/dm/new"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-black text-sm font-bold hover:bg-primary/90 transition-colors"
          >
            <Plus size={16} />
            {t('dm.conversations.newDm')}
          </Link>
        </div>
      ) : (
        <ul className="space-y-2">
          {allItems.map((c) => {
            const peerId = c.peerUserId;
            const previewTime = c?.latestMessage?.createdAt || c.updatedAt;
            return (
              <li key={c.conversationId}>
                <Link
                  to={`/dm/c/${c.conversationId}`}
                  className="flex items-center gap-3 p-3 sm:p-4 rounded-xl bg-dark-card border border-dark-border hover:border-primary/40 transition-colors"
                >
                  <UserAvatar user={c.peerAvatar} size={44} className="w-10 h-10 sm:w-11 sm:h-11" variant="small" />
                  <div className="min-w-0 flex-grow">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-white truncate">
                        {peerId != null
                          ? t('dm.chat.peer', { userId: peerId })
                          : c.pairKey}
                      </p>
                      <span className="text-[11px] text-dark-text-muted flex-shrink-0">
                        {formatRelative(previewTime)}
                      </span>
                    </div>
                    <p className="text-xs text-dark-text-secondary truncate">
                      {getPreviewText(c)}
                    </p>
                  </div>
                  {c.unreadCount > 0 && (
                    <span className="ml-2 inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 rounded-full bg-primary text-black text-xs font-bold">
                      {c.unreadCount}
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      {query.hasNextPage && (
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
            className="px-4 py-2 rounded-lg bg-dark-surface border border-dark-border text-dark-text-secondary text-sm hover:border-primary/40 hover:text-primary transition-colors disabled:opacity-50"
          >
            {query.isFetchingNextPage ? t('common.loading') : t('dm.conversations.loadMore')}
          </button>
        </div>
      )}
    </div>
  );
}
